

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Rating, TextField, Button, Chip, Avatar, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, LinearProgress, Skeleton, Snackbar, Alert,
  Switch, FormControlLabel, Divider, Tooltip, Fade,
  CircularProgress, Paper, Stack,
} from "@mui/material";
import {
  Star, StarBorder, Send, ThumbUp, Delete, Edit,
  Close, CheckCircle, MedicalServices, LocalPharmacy,
  Science, Groups, Campaign, FilterList, Add, ArrowBack,
  Verified, EmojiEvents,
} from "@mui/icons-material";
import axios from "axios";

const API = process.env.REACT_APP_API_BASE_URL;

// ─── Service config (matches your ServiceSelectionScreen SERVICES list) ──────

const SERVICES = [
    {
      key:   "medicine_delivery",
      label: "Medicine Delivery",
      icon:  <LocalPharmacy />,
      emoji: "💊",
      color: "#006D77",
      grad:  "linear-gradient(135deg,#006D77 0%,#004E57 100%)",
      light: "#EEF7F8",
      accent: "#80ED99",
    },
    {
      key:   "diagnostic",
      label: "Diagnostic Services",
      icon:  <Science />,
      emoji: "🔬",
      color: "#008B98",
      grad:  "linear-gradient(135deg,#008B98 0%,#006D77 100%)",
      light: "#E6F4F6",
      accent: "#80ED99",
    },
    {
      key:   "nursing",
      label: "Nursing & Physiotherapy",
      icon:  <MedicalServices />,
      emoji: "🩺",
      color: "#005F68",
      grad:  "linear-gradient(135deg,#006D77 0%,#003D45 100%)",
      light: "#E0EFF1",
      accent: "#80ED99",
    },
    {
      key:   "package",
      label: "Health Packages",
      icon:  <CheckCircle />,
      emoji: "📦",
      color: "#00A3B4",
      grad:  "linear-gradient(135deg,#00A3B4 0%,#006D77 100%)",
      light: "#E0F5F7",
      accent: "#80ED99",
    },
    {
      key:   "camp",
      label: "Health Camps",
      icon:  <Campaign />,
      emoji: "🏕️",
      color: "#004E57",
      grad:  "linear-gradient(135deg,#008B98 0%,#003D45 100%)",
      light: "#E0EFF1",
      accent: "#80ED99",
    },
  ];

// ─── Auth helpers  (matches your existing sessionStorage pattern) ─────────────
const getToken  = () => sessionStorage.getItem("token");
const getUserId = () => sessionStorage.getItem("userId");
const authHead  = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const isLoggedIn = () => !!getToken() && !!getUserId();

// ─── Rating label helper ──────────────────────────────────────────────────────
const ratingLabel = (v) =>
  ["", "Poor 😞", "Fair 😐", "Good 🙂", "Great 😊", "Excellent 🤩"][Math.round(v)] || "";

// ─────────────────────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/** Horizontal rating distribution bar */
const RatingBar = ({ star, pct, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748B", width: 10 }}>{star}</Typography>
    <Star sx={{ color: "#FFB703", fontSize: 13 }} />
    <Box sx={{ flex: 1, height: 7, borderRadius: 4, bgcolor: "#F1F5F9", overflow: "hidden" }}>
      <Box
        sx={{
          width: `${pct}%`, height: "100%", borderRadius: 4,
          bgcolor: color, transition: "width 1s ease",
        }}
      />
    </Box>
    <Typography sx={{ fontSize: 11, color: "#94A3B8", width: 30, textAlign: "right" }}>{pct}%</Typography>
  </Box>
);

/** Stats panel — avg rating + distribution */
const StatsPanel = ({ stats, svc }) => {
  if (!stats) return <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 3 }} />;
  return (
    <Card sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
      {/* Gradient header */}
      <Box sx={{ background: svc.grad, p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: 2.5, p: 1.2, display: "flex" }}>
          {React.cloneElement(svc.icon, { sx: { color: "#fff", fontSize: 26 } })}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, color: "#fff", fontSize: 15 }}>
            {svc.label}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
            {stats.total} review{stats.total !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, color: "#fff", fontSize: 34, lineHeight: 1 }}>
            {stats.avg_rating || "—"}
          </Typography>
          <Rating
            readOnly value={stats.avg_rating || 0} precision={0.5} size="small"
            icon={<Star sx={{ color: "#FFE066" }} fontSize="inherit" />}
            emptyIcon={<StarBorder sx={{ color: "rgba(255,255,255,0.5)" }} fontSize="inherit" />}
          />
        </Box>
      </Box>
      {/* Distribution */}
      <CardContent sx={{ p: "16px 20px 18px !important" }}>
        {[5, 4, 3, 2, 1].map(n => (
          <RatingBar key={n} star={n} pct={(stats.distribution || {})[n] || 0} color={svc.color} />
        ))}
      </CardContent>
    </Card>
  );
};
const getUserRole = () => sessionStorage.getItem("role"); 
const isResident  = () => getUserRole() === "resident"; 
/** Single feedback card */
const FeedbackCard = ({ fb, isOwn, onDelete, onHelpful, svc, onEdit }) => {
    const [helping, setHelping] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const handleHelpful = async () => {
      setHelping(true);
      await onHelpful(fb.feedback_id);
      setHelping(false);
    };
  
    const COMMENT_LIMIT = 120;
    const isLong = fb.comment && fb.comment.length > COMMENT_LIMIT;
    const displayComment = isLong && !expanded
      ? fb.comment.slice(0, COMMENT_LIMIT) + "…"
      : fb.comment;
  
    // Star color based on rating
    const starColor = fb.rating >= 4 ? "#006D77" : fb.rating >= 3 ? "#F59E0B" : "#EF4444";
  
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #E2EEF0",
          borderLeft: `4px solid ${svc.color}`,
          bgcolor: "#FFFFFF",
          transition: "all 0.22s ease",
          "&:hover": {
            boxShadow: "0 6px 24px rgba(0,109,119,0.12)",
            transform: "translateY(-1px)",
            borderColor: "#B2D8DC",
            borderLeftColor: svc.color,
          },
        }}
      >
        <CardContent sx={{ p: "18px 20px 14px 20px" }}>
  
          {/* ── Top row: avatar + name + date + star badge ── */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {/* Avatar circle */}
              <Avatar
                sx={{
                  bgcolor: svc.color,
                  color: "#fff",
                  width: 38, height: 38,
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 800, fontSize: 15,
                  flexShrink: 0,
                }}
              >
                {fb.user_name?.[0]?.toUpperCase() || "U"}
              </Avatar>
  
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <Typography sx={{
                    fontFamily: "'Sora',sans-serif", fontWeight: 700,
                    fontSize: 14, color: "#0F172A", lineHeight: 1.2,
                  }}>
                    {fb.user_name || "User"}
                  </Typography>
                  {fb.is_verified && (
                    <Tooltip title="Verified review">
                      <Verified sx={{ fontSize: 13, color: svc.color }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography sx={{ fontSize: 11, color: "#94A3B8", mt: 0.2 }}>
                  {new Date(fb.created_at).toLocaleDateString("en-IN", {
                    day: "numeric", month: "short", year: "numeric",
                  })}
                </Typography>
              </Box>
            </Box>
  
            {/* Right: star rating badge + actions */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              {/* Star badge */}
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.4,
                bgcolor: starColor + "15",
                border: `1px solid ${starColor}30`,
                borderRadius: 2, px: 1.2, py: 0.4,
              }}>
                <Star sx={{ fontSize: 13, color: starColor }} />
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: starColor }}>
                  {fb.rating.toFixed(1)}
                </Typography>
              </Box>
  
              {isOwn && (
                <Box sx={{ display: "flex" }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(fb)}
                      sx={{ color: "#94A3B8", p: 0.5, "&:hover": { color: svc.color, bgcolor: svc.light } }}>
                      <Edit sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => onDelete(fb.feedback_id)}
                      sx={{ color: "#94A3B8", p: 0.5, "&:hover": { color: "#EF4444", bgcolor: "#FEF2F2" } }}>
                      <Delete sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
  
          {/* ── Star row visual ── */}
          <Box sx={{ mb: 1 }}>
            <Rating
              readOnly value={fb.rating} precision={0.5} size="small"
              icon={<Star sx={{ color: starColor, fontSize: 15 }} />}
              emptyIcon={<StarBorder sx={{ color: "#E2E8F0", fontSize: 15 }} />}
            />
          </Box>
  
          {/* ── Title ── */}
          {fb.title && (
            <Typography sx={{
              fontFamily: "'Sora',sans-serif", fontWeight: 700,
              fontSize: 14, color: "#0F172A", mb: 0.6, lineHeight: 1.4,
            }}>
              {fb.title}
            </Typography>
          )}
  
          {/* ── Comment (expandable height, not width) ── */}
          {fb.comment && (
            <Box>
              <Typography sx={{
                fontSize: 13, color: "#475569", lineHeight: 1.75,
                mb: isLong ? 0.5 : 1.2,
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>
                {displayComment}
              </Typography>
              {isLong && (
                <Button
                  size="small"
                  onClick={() => setExpanded(p => !p)}
                  sx={{
                    fontSize: 11, fontWeight: 700, color: svc.color,
                    textTransform: "none", p: 0, minWidth: 0, mb: 1,
                    "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                  }}
                >
                  {expanded ? "Show less ↑" : "Read more ↓"}
                </Button>
              )}
            </Box>
          )}
  
          {/* ── Tags ── */}
          {fb.tags?.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mb: 1.2 }}>
              {fb.tags.map((t, i) => (
                <Chip key={i} label={t.label} size="small"
                  sx={{
                    fontSize: 10, fontWeight: 700, borderRadius: 1.5, height: 22,
                    bgcolor: t.positive ? "#EEF7F8" : "#FEF2F2",
                    color:   t.positive ? "#006D77" : "#DC2626",pointerEvents:"none",
                    border:  `1px solid ${t.positive ? "#B2D8DC" : "#FECACA"}`,
                  }}
                />
              ))}
            </Box>
          )}
  
          {/* ── Reference ID ── */}
          {fb.reference_id && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={`# ${fb.reference_id}`} size="small"
                sx={{
                  fontSize: 10, fontWeight: 600, height: 20,
                  bgcolor: "#F8FAFC", color: "#94A3B8",
                  border: "1px solid #E2E8F0", borderRadius: 1,pointerEvents:"none"
                }}
              />
            </Box>
          )}
  
          {/* ── Admin reply ── */}
          {fb.admin_reply && (
            <Box sx={{
              bgcolor: "#EEF7F8", border: "1px solid #B2D8DC",
              borderLeft: "3px solid #006D77",
              borderRadius: "0 8px 8px 0", p: "10px 14px", mb: 1.2,
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#006D77", mb: 0.4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                🏥 Ayuja Team
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#004E57", lineHeight: 1.65 }}>
                {fb.admin_reply}
              </Typography>
            </Box>
          )}
  
          {/* ── Footer: helpful ── */}
          <Divider sx={{ borderColor: "#F1F5F9", mb: 1 }} />
          <Button
            size="small"
            startIcon={
              helping
                ? <CircularProgress size={11} sx={{ color: "#006D77" }} />
                : <ThumbUp sx={{ fontSize: "12px !important", color: "#94A3B8" }} />
            }
            onClick={handleHelpful}
            disabled={helping}
            sx={{
              fontSize: 11, fontWeight: 600, color: "#94A3B8",
              textTransform: "none", px: 1, borderRadius: 1.5,
              "&:hover": { bgcolor: "#EEF7F8", color: "#006D77" },
            }}
          >
            Helpful{fb.helpful_count > 0 ? ` · ${fb.helpful_count}` : ""}
          </Button>
        </CardContent>
      </Card>
    );
  };
// ─────────────────────────────────────────────────────────────────────────────
//  Submit / Edit Dialog
// ─────────────────────────────────────────────────────────────────────────────

const FeedbackDialog = ({ open, onClose, onSuccess, preService, editData }) => {
  const isEdit = !!editData;

  const [step,     setStep]     = useState(isEdit ? 2 : 1);
  const [service,  setService]  = useState(preService || editData?.service_type || "");
  const [rating,   setRating]   = useState(editData?.rating || 0);
  const [hover,    setHover]    = useState(-1);
  const [title,    setTitle]    = useState(editData?.title || "");
  const [comment,  setComment]  = useState(editData?.comment || "");
  const [refId,    setRefId]    = useState(editData?.reference_id || "");
  const [selTags,  setSelTags]  = useState((editData?.tags || []).map(t => t.label));
  const [anon,     setAnon]     = useState(editData?.is_anonymous || false);
  const [tags,     setTags]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [done,     setDone]     = useState(false);

  // Reset when dialog opens
  useEffect(() => {
    if (!open) return;
    setDone(false); setError("");
    if (editData) {
      setStep(2);
      setService(editData.service_type || "");
      setRating(editData.rating || 0);
      setTitle(editData.title || "");
      setComment(editData.comment || "");
      setRefId(editData.reference_id || "");
      setSelTags((editData.tags || []).map(t => t.label));
      setAnon(editData.is_anonymous || false);
    } else {
      setStep(preService ? 2 : 1);
      setService(preService || "");
      setRating(0); setTitle(""); setComment("");
      setRefId(""); setSelTags([]); setAnon(false);
    }
  }, [open, preService, editData]);

  // Load tags when service selected
  useEffect(() => {
    if (!service) return;
    axios.get(`${API}/feedback/tags/${service}`)
      .then(r => setTags(r.data.tags || []))
      .catch(() => setTags([]));
  }, [service]);

  const svc = SERVICES.find(s => s.key === service);
  const toggleTag = (lbl) =>
    setSelTags(p => p.includes(lbl) ? p.filter(x => x !== lbl) : [...p, lbl]);

  const handleSubmit = async () => {
    if (!rating) { setError("Please give a star rating."); return; }
    if (!isLoggedIn()) { setError("Please log in to submit feedback."); return; }
    setLoading(true); setError("");
    try {
      if (isEdit) {
        await axios.put(
          `${API}/feedback/${editData.feedback_id}`,
          { rating, title, comment, is_anonymous: anon },
          authHead()
        );
      } else {
        await axios.post(
          `${API}/feedback/submit`,
          { service_type: service, rating, title, comment, tags: selTags, is_anonymous: anon, reference_id: refId },
          authHead()
        );
      }
      setDone(true);
      setTimeout(() => { onSuccess(); onClose(); }, 1800);
    } catch (e) {
      setError(e.response?.data?.error || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>

      {/* Header */}
      <Box sx={{ background: svc ? svc.grad : "linear-gradient(135deg,#006D77,#003D45)", p: "20px 24px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, color: "#fff", fontSize: 18 }}>
            {done ? "🎉 Thank You!" : isEdit ? "Edit Your Review" : "Share Your Experience"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.8)" }}>
            <Close />
          </IconButton>
        </Box>
        {svc && !done && (
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.72)", mt: 0.5 }}>
            {svc.emoji} {svc.label}
          </Typography>
        )}
      </Box>

      <DialogContent sx={{ p: 3 }}>

        {/* Success */}
        {done && (
          <Fade in>
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography sx={{ fontSize: 60, mb: 2 }}>🙏</Typography>
              <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0F172A", mb: 1 }}>
                {isEdit ? "Review Updated!" : "Feedback Submitted!"}
              </Typography>
              <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                Your feedback helps us improve for everyone.
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Step 1 — Pick service */}
        {!done && step === 1 && (
          <Box>
            <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, mb: 2.5, color: "#0F172A" }}>
              Which service are you reviewing?
            </Typography>
            <Grid container spacing={1.5}>
              {SERVICES.map(s => (
                <Grid item xs={12} sm={6} key={s.key}>
                  <Card
                    onClick={() => { setService(s.key); setStep(2); }}
                    sx={{
                      cursor: "pointer", border: "2px solid",
                      borderColor: service === s.key ? s.color : "#F1F5F9",
                      borderRadius: 3, transition: "all 0.2s",
                      "&:hover": {
                        borderColor: s.color,
                        boxShadow:   `0 0 0 3px ${s.color}22`,
                        bgcolor:     s.light,
                      },
                    }}
                  >
                    <CardContent sx={{ p: "14px 16px !important", display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ color: s.color, display: "flex" }}>{s.icon}</Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{s.label}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Step 2 — Write review */}
        {!done && step === 2 && (
          <Box>
            {/* Star rating */}
            <Box sx={{ textAlign: "center", mb: 3, py: 1 }}>
              <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, mb: 1.5, color: "#0F172A", fontSize: 15 }}>
                How would you rate us?
              </Typography>
              <Rating
                value={rating} size="large"
                onChange={(_, v) => setRating(v)}
                onChangeActive={(_, v) => setHover(v)}
                icon={<Star sx={{ color: "#FFB703", fontSize: 46 }} />}
                emptyIcon={<StarBorder sx={{ color: "#CBD5E1", fontSize: 46 }} />}
              />
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: svc?.color || "#006D77", mt: 0.5, minHeight: 24 }}>
                {ratingLabel(hover !== -1 ? hover : rating)}
              </Typography>
            </Box>

            {/* Quick tags */}
            {tags.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748B", mb: 1.2,
                  textTransform: "uppercase", letterSpacing: 0.6 }}>
                  What stood out?
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                  {tags.map(t => {
                    const sel = selTags.includes(t.label);
                    return (
                      <Chip key={t.label} label={t.label} clickable size="small"
                        onClick={() => toggleTag(t.label)}
                        sx={{
                          fontSize: 11, fontWeight: 700, borderRadius: 1.5, transition: "all 0.18s",
                          bgcolor: sel
                            ? (t.positive ? "#059669" : "#DC2626")
                            : (t.positive ? "#ECFDF5" : "#FEF2F2"),
                          color: sel ? "#fff" : (t.positive ? "#059669" : "#DC2626"),
                          pointerEvents:"none",border: `1px solid ${t.positive ? "#A7F3D0" : "#FECACA"}`,
                          "&:hover": { opacity: 0.85 },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Title */}
            <TextField fullWidth label="Give it a headline (optional)" variant="outlined" size="small"
              value={title} onChange={e => setTitle(e.target.value)} inputProps={{ maxLength: 120 }}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

            {/* Comment */}
            <TextField fullWidth multiline rows={3}
              label="Tell us more about your experience (optional)" variant="outlined"
              value={comment} onChange={e => setComment(e.target.value)}
              inputProps={{ maxLength: 1000 }}
              helperText={`${comment.length}/1000`}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

            {/* Reference ID */}
            {!isEdit && (
              <TextField fullWidth size="small"
                label="Order / Booking ID (optional — to verify your review)"
                variant="outlined" value={refId} onChange={e => setRefId(e.target.value)}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            )}

            {/* Anonymous toggle */}
            <FormControlLabel
              control={<Switch checked={anon} onChange={e => setAnon(e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: 13, color: "#64748B" }}>Post anonymously</Typography>}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>
            )}
          </Box>
        )}
      </DialogContent>

      {!done && step === 2 && (
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {!isEdit && (
            <Button onClick={() => setStep(1)}
              sx={{ color: "#64748B", textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
              ← Back
            </Button>
          )}
          <Button variant="contained" onClick={handleSubmit}
            disabled={loading || !rating}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Send />}
            sx={{
              background: svc?.grad || "linear-gradient(135deg,#006D77,#003D45)",
              textTransform: "none", fontWeight: 800, borderRadius: 2.5, px: 3, fontSize: 14,
              "&:hover": { opacity: 0.9 }, "&:disabled": { opacity: 0.55 },
            }}
          >
            {loading ? "Submitting…" : isEdit ? "Update Review" : "Submit Feedback"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

const FeedbackPage = () => {
  const navigate = useNavigate();

  // ── State ──
  const [activeTab,    setActiveTab]    = useState(0);
  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [editData,     setEditData]     = useState(null);
  const [preService,   setPreService]   = useState("");
  const [allStats,     setAllStats]     = useState({});
  const [feedbacks,    setFeedbacks]    = useState([]);
  const [myFeedbacks,  setMyFeedbacks]  = useState([]);
  const [loadingFeed,  setLoadingFeed]  = useState(false);
  const [sort,         setSort]         = useState("newest");
  const [viewMode,     setViewMode]     = useState("all");   // "all" | "mine"
  const [snack,        setSnack]        = useState({ open: false, msg: "", sev: "success" });
  const [showCount, setShowCount] = useState(2);
  const activeSvc = SERVICES[activeTab];

  // ── Load all-service stats once ──
  useEffect(() => {
    axios.get(`${API}/feedback/stats/all`)
      .then(r => setAllStats(r.data.stats || {}))
      .catch(() => {});
  }, []);

  // ── Load public feed ──

const loadFeed = useCallback(async () => {
  setLoadingFeed(true);
  try {
    const role = getUserRole();

    if (role === "resident") {
      // Residents: only their own feedback
      if (!isLoggedIn()) { setFeedbacks([]); setLoadingFeed(false); return; }
      const r = await axios.get(
        `${API}/feedback/my?service_type=${activeSvc.key}&per_page=50`,
        authHead()
      );
      setFeedbacks(r.data.feedbacks || []);

    } else if (role === "admin") {
      // Admin: all feedback via admin endpoint
      const r = await axios.get(
        `${API}/feedback/all?service_type=${activeSvc.key}&sort=${sort}&per_page=50`,
        authHead()
      );
      setFeedbacks(r.data.feedbacks || []);

    } else {
      // Other logged-in users: public feed
      const r = await axios.get(
        `${API}/feedback/service/${activeSvc.key}?sort=${sort}&per_page=20`
      );
      setFeedbacks(r.data.feedbacks || []);
    }
  } catch { setFeedbacks([]); }
  setLoadingFeed(false);
}, [activeSvc.key, sort]);




  // ── Load my feed (JWT protected) ──
  const loadMyFeed = useCallback(async () => {
    if (!isLoggedIn()) return;
    try {
      const r = await axios.get(
        `${API}/feedback/my?service_type=${activeSvc.key}&per_page=50`,
        authHead()
      );
      setMyFeedbacks(r.data.feedbacks || []);
    } catch { setMyFeedbacks([]); }
  }, [activeSvc.key]);

  useEffect(() => { loadFeed(); loadMyFeed(); }, [loadFeed, loadMyFeed]);

  // ── Handlers ──
  const handleDelete = async (fbId) => {
    try {
      await axios.delete(`${API}/feedback/${fbId}`, authHead());
      loadFeed(); loadMyFeed();
      setSnack({ open: true, msg: "Feedback deleted.", sev: "info" });
    } catch {
      setSnack({ open: true, msg: "Could not delete. Try again.", sev: "error" });
    }
  };

  const handleHelpful = async (fbId) => {
    try { await axios.put(`${API}/feedback/${fbId}/helpful`, {}, authHead()); loadFeed(); }
    catch {}
  };

  const openSubmit = (svcKey) => {
    if (!isLoggedIn()) {
      setSnack({ open: true, msg: "Please log in to submit feedback.", sev: "warning" });
      return;
    }
    setEditData(null);
    setPreService(svcKey || activeSvc.key);
    setDialogOpen(true);
  };

  const openEdit = (fb) => {
    setEditData(fb);
    setPreService("");
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    loadFeed(); loadMyFeed();
    // Refresh stats
    axios.get(`${API}/feedback/stats/all`)
      .then(r => setAllStats(r.data.stats || {})).catch(() => {});
    setSnack({ open: true, msg: "🎉 Thank you for your feedback!", sev: "success" });
  };

  // const displayFbs = viewMode === "mine" ? myFeedbacks : feedbacks;
  const currentUserId = getUserId();
  const role = getUserRole();
  
  const displayFbs = role === "admin"
    ? feedbacks  // admin sees all
    : feedbacks.filter(fb => fb.user_id === currentUserId); 
  const activeStats = allStats[activeSvc.key];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", fontFamily: "'DM Sans',sans-serif" }}>

      {/* ── Page hero header ── */}
      <Box sx={{ background: "linear-gradient(135deg,#003D45 0%,#006D77 55%,#008B98 100%)", pb: 5, pt: 4, px: 3 }}>
        <Container maxWidth="lg">
          {/* Back button */}
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontWeight: 600, mb: 2, p: 0,
              "&:hover": { color: "#fff", bgcolor: "transparent" } }}
          >
            Back
          </Button>

          <Typography sx={{
            fontFamily: "'Sora',sans-serif", fontWeight: 900,
            fontSize: { xs: 26, md: 36 }, color: "#fff", mb: 0.8,
          }}>
            Feedback & Reviews
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 14, mb: 3.5, maxWidth: 500 }}>
            Your honest feedback helps us deliver better care to your whole community.
          </Typography>

          {/* Quick-action service tiles */}
          <Grid container spacing={1.5}>
            {SERVICES.map(s => {
              const st = allStats[s.key];
              return (
                <Grid item xs={6} sm={4} md key={s.key}>
                  <Card
                    onClick={() => openSubmit(s.key)}
                    sx={{
                      cursor: "pointer", borderRadius: 3,
                      background: "rgba(255,255,255,0.1)",
                      backdropFilter: "blur(14px)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      transition: "all 0.25s",
                      "&:hover": { background: "rgba(255,255,255,0.2)", transform: "translateY(-3px)" },
                    }}
                  >
                    <CardContent sx={{ p: "14px 12px !important", textAlign: "center" }}>
                      <Typography sx={{ fontSize: 24, mb: 0.5 }}>{s.emoji}</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#fff", mb: 0.3 }}>
                        {s.label}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>
                        {st && st.total > 0
                          ? `${st.avg_rating}★ · ${st.total} reviews`
                          : "Be the first!"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ── Content ── */}
      <Container maxWidth="lg" sx={{ py: 5 }}>

        {/* Service tabs */}
        <Paper
          elevation={0}
          sx={{ borderRadius: 3, mb: 4, border: "1px solid #F1F5F9", overflow: "hidden" }}
        >
          <Tabs
            value={activeTab}
            // onChange={(_, v) => { setActiveTab(v); setViewMode("all"); }}
            onChange={(_, v) => { setActiveTab(v); setViewMode(isResident() ? "mine" : "all"); setShowCount(2); }}
            variant="scrollable" scrollButtons="auto"
            sx={{
              "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
                fontSize: 13, minWidth: 130, py: 2 },
              "& .Mui-selected": { color: `${activeSvc.color} !important` },
              "& .MuiTabs-indicator": { bgcolor: activeSvc.color, height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            {SERVICES.map((s, i) => {
              const st = allStats[s.key];
              return (
                <Tab key={s.key} label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    {React.cloneElement(s.icon, { sx: { fontSize: 17 } })}
                    <span>{s.label}</span>
                    {st && st.avg_rating > 0 && (
                      <Chip label={st.avg_rating} size="small"
                        sx={{ fontSize: 10, height: 18, px: 0.3, bgcolor: s.color + "22", color: s.color, fontWeight: 800,pointerEvents:"none" }} />
                    )}
                  </Box>
                } />
              );
            })}
          </Tabs>
        </Paper>

        <Grid container spacing={3}>

          {/* ── LEFT: stats + submit CTA + my review count ── */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <StatsPanel stats={activeStats} svc={activeSvc} />

              <Button
                fullWidth variant="contained"
                startIcon={<Add />}
                onClick={() => openSubmit(activeSvc.key)}
                sx={{
                  background: activeSvc.grad, borderRadius: 3, py: 1.6,
                  fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14,
                  textTransform: "none",
                  boxShadow: `0 4px 18px ${activeSvc.color}44`,
                  "&:hover": { opacity: 0.9, boxShadow: `0 8px 26px ${activeSvc.color}66` },
                }}
              >
                Write a Review
              </Button>

              {/* My reviews summary */}
              {isLoggedIn() && myFeedbacks.length > 0 && (
                <Paper elevation={0} sx={{
                  border: "1px solid #F1F5F9", borderRadius: 3,
                  p: 2, display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
                      My reviews for {activeSvc.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>
                      Avg: {(myFeedbacks.reduce((s, f) => s + f.rating, 0) / myFeedbacks.length).toFixed(1)} ★
                    </Typography>
                  </Box>
                  <Chip
                    label={myFeedbacks.length}
                    sx={{ bgcolor: activeSvc.color + "22", color: activeSvc.color, fontWeight: 800, fontSize: 14 ,pointerEvents:"none"}}
                  />
                </Paper>
              )}

              {/* Tip card */}
              <Paper elevation={0} sx={{
                border: `1px solid ${activeSvc.color}33`, borderRadius: 3, p: 2,
                bgcolor: activeSvc.light,
              }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: activeSvc.color, mb: 0.5 }}>
                  <EmojiEvents sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                  Why review?
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#475569", lineHeight: 1.65 }}>
                  Reviews help other patients choose the right service and help Ayuja teams improve care quality.
                </Typography>
              </Paper>
            </Stack>
          </Grid>

          {/* ── RIGHT: feedback feed ── */}
          <Grid item xs={12} md={8}>

            {/* Filter bar */}
            <Box sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              mb: 2.5, flexWrap: "wrap", gap: 1.5,
            }}>


              {/* All / Mine toggle — hidden for residents */}
              {!isResident() && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  {["all", "mine"].map(m => (
                    <Chip key={m}
                      label={m === "all" ? `All Reviews (${feedbacks.length})` : `My Reviews (${myFeedbacks.length})`}
                      clickable
                      onClick={() => { setViewMode(m); setShowCount(2); }}
                      sx={{
                        fontWeight: 700, fontSize: 12, borderRadius: 2,
                        bgcolor: viewMode === m ? activeSvc.color : "#F1F5F9",
                        color:   viewMode === m ? "#fff"           : "#64748B",
                        "&:hover": { opacity: 0.85 },
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Sort chips */}
              <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
                <FilterList sx={{ color: "#94A3B8", fontSize: 16 }} />
                {[
                  { key: "newest",  label: "Newest" },
                  { key: "highest", label: "Highest" },
                  { key: "lowest",  label: "Lowest" },
                ].map(s => (
                  <Chip key={s.key} label={s.label} clickable size="small"
                    onClick={() => setSort(s.key)}
                    sx={{
                      fontSize: 11, fontWeight: 700, borderRadius: 1.5,
                      bgcolor: sort === s.key ? "#0F172A" : "#F1F5F9",
                      color:   sort === s.key ? "#fff"    : "#64748B",
                    }}
                  />
                ))}
              </Box>
            </Box>
           

            {/* Feed content */}
            {loadingFeed ? (
            <Stack spacing={2}>
                {[...Array(2)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={150} sx={{ borderRadius: 3 }} />
                ))}
            </Stack>
            ) : displayFbs.length === 0 ? (
            <Paper elevation={0} sx={{
                border: "1px dashed #B2D8DC", borderRadius: 4,
                py: 8, textAlign: "center", bgcolor: "#FAFCFC",
            }}>
                <Typography sx={{ fontSize: 40, mb: 1.5 }}>
                {viewMode === "mine" ? "📝" : activeSvc.emoji}
                </Typography>
                <Typography sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, color: "#0F172A", mb: 0.8, fontSize: 16 }}>
                {viewMode === "mine" ? "No reviews from you yet" : "No reviews yet"}
                </Typography>
                <Typography sx={{ color: "#64748B", mb: 3, fontSize: 13 }}>
                {viewMode === "mine"
                    ? "Share your experience to help others!"
                    : `Be the first to review ${activeSvc.label}!`}
                </Typography>
                <Button variant="contained" onClick={() => openSubmit(activeSvc.key)}
                sx={{
                    background: activeSvc.grad, textTransform: "none",
                    fontWeight: 700, borderRadius: 2.5, px: 3,
                }}>
                Write First Review
                </Button>
            </Paper>
            ) : (
            <>
                <Stack spacing={1.5}>
                {displayFbs.slice(0, showCount).map(fb => (
                    <FeedbackCard
                    key={fb.feedback_id}
                    fb={fb}
                    svc={activeSvc}
                    // isOwn={viewMode === "mine"}
                    isOwn={fb.user_id === getUserId()}
                    onDelete={handleDelete}
                    onHelpful={handleHelpful}
                    onEdit={openEdit}
                    />
                ))}
                </Stack>

                {/* Show more / show less */}
                {displayFbs.length > 2 && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                    {showCount < displayFbs.length ? (
                    <Button
                        variant="outlined"
                        onClick={() => setShowCount(p => p + 5)}
                        sx={{
                        borderColor: activeSvc.color, color: activeSvc.color,
                        textTransform: "none", fontWeight: 700, borderRadius: 2.5,
                        px: 4, fontSize: 13,
                        "&:hover": { bgcolor: activeSvc.light, borderColor: activeSvc.color },
                        }}
                    >
                        Show more ({displayFbs.length - showCount} remaining)
                    </Button>
                    ) : (
                    <Button
                        variant="text"
                        onClick={() => setShowCount(2)}
                        sx={{ color: "#94A3B8", textTransform: "none", fontWeight: 600, fontSize: 12 }}
                    >
                        Show less ↑
                    </Button>
                    )}
                </Box>
                )}
            </>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* ── Dialog ── */}
      <FeedbackDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditData(null); }}
        onSuccess={handleSuccess}
        preService={preService}
        editData={editData}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.sev} sx={{ borderRadius: 3, fontWeight: 600, fontSize: 13 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackPage;
